import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  Calendar,
  Database,
  Instagram,
  MessageCircle,
  ShoppingBag,
} from "lucide-react";
import { MessengerIcon } from "@/components/icons/MessengerIcon";
import { Button } from "@/components/ui/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";
import { useInView } from "@/hooks/useInView";
import { cn } from "@/lib/utils";
import { SHOPIFY_UI_ENABLED } from "@/lib/shopify-ui-flag";

type IntegrationCardItem = {
  id: string;
  name: string;
  icon: ReactNode;
  comingSoon?: boolean;
  isCustom?: boolean;
};

function ChatGptLogo({ className }: { className?: string }) {
  return (
    <i
      className={cn("bi bi-openai shrink-0 text-[2rem] leading-none text-slate-900", className)}
      aria-hidden
    />
  );
}

function GeminiLogo({ className, gradId = "gemini-grad-landing" }: { className?: string; gradId?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden>
      <defs>
        <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#4285f4" />
          <stop offset="50%" stopColor="#9b72cb" />
          <stop offset="100%" stopColor="#d96570" />
        </linearGradient>
      </defs>
      <path
        fill={`url(#${gradId})`}
        d="M12 2c1.2 3.8 3.8 6.4 7.6 7.6C15.4 10.8 12.8 13.4 12 17c-.8-3.6-3.4-6.2-7.6-7.4C8.6 8.4 11.2 5.8 12 2z"
      />
      <path
        fill={`url(#${gradId})`}
        d="M5 12c3.2.6 5.6 3 6.2 6.2C10.6 15 8.2 12.6 5 12zm14 0c-3.2.6-5.6 3-6.2 6.2.6-3.2 3-5.6 6.2-6.2z"
        opacity="0.85"
      />
    </svg>
  );
}

const integrations: IntegrationCardItem[] = [
  {
    id: "messenger",
    name: "Messenger",
    icon: <MessengerIcon className="text-[#0084FF]" size={32} />,
    comingSoon: true,
  },
  {
    id: "instagram",
    name: "Instagram",
    icon: <Instagram className="h-8 w-8 text-pink-600" strokeWidth={1.5} />,
  },
  {
    id: "gemini",
    name: "Gemini",
    icon: <GeminiLogo className="h-8 w-8" gradId="gemini-grad-landing" />,
  },
  {
    id: "calendar",
    name: "Google Calendar",
    icon: <Calendar className="h-8 w-8 text-amber-700" strokeWidth={1.5} />,
  },
  {
    id: "whatsapp",
    name: "WhatsApp",
    icon: <MessageCircle className="h-8 w-8 text-[#25D366]" strokeWidth={1.5} />,
  },
  {
    id: "chatgpt",
    name: "ChatGPT",
    icon: <ChatGptLogo />,
  },
  ...(SHOPIFY_UI_ENABLED
    ? [
        {
          id: "shopify",
          name: "Shopify",
          icon: <ShoppingBag className="h-8 w-8 text-[#5c6ac4]" strokeWidth={1.5} />,
        } satisfies IntegrationCardItem,
      ]
    : []),
  {
    id: "erp",
    name: "ERP personalizado",
    icon: <Database className="h-8 w-8 text-primary" strokeWidth={1.5} />,
    isCustom: true,
  },
];

const DEFAULT_CENTER_INDEX = integrations.findIndex((i) => i.id === "whatsapp");
const AUTOPLAY_MS = 5000;

/** Canto dobrado estilo referência: losango cortado no topo-direito */
function CardDogEar() {
  return (
    <div
      className="pointer-events-none absolute right-0 top-0 z-20 size-7 overflow-hidden rounded-tr-2xl"
      aria-hidden
    >
      <div className="absolute -right-3.5 -top-3.5 size-7 rotate-45 bg-slate-100 shadow-[0_1px_2px_rgba(15,23,42,0.08)] ring-1 ring-slate-200/60" />
    </div>
  );
}

function DogEarCard({
  item,
  isCenter,
  isInView,
}: {
  item: IntegrationCardItem;
  isCenter: boolean;
  isInView: boolean;
}) {
  return (
    <div className={cn("relative inline-flex", isCenter && "py-4")}>
      {isCenter ? (
        <div
          className="pointer-events-none absolute inset-0 -z-10 rounded-2xl bg-primary/10 blur-2xl"
          aria-hidden
        />
      ) : null}

      <article
        className={cn(
          "relative z-10 flex w-[168px] shrink-0 flex-col items-center rounded-2xl border bg-white px-4 pb-4 pt-8 shadow-[0_4px_20px_-6px_rgba(15,23,42,0.1)] transition-all duration-500 sm:w-[180px]",
          isCenter
            ? "scale-[1.03] border-primary/35 shadow-[0_12px_32px_-8px_hsl(var(--primary)/0.22)]"
            : "border-slate-200/80",
          !isCenter && !isInView && "opacity-0",
          !isCenter && isInView && "opacity-45 sm:opacity-50",
          isCenter && isInView && "opacity-100",
          !isInView && "translate-y-4 opacity-0",
          isInView && "translate-y-0",
        )}
      >
        <CardDogEar />
        {isCenter ? (
          <>
            <span
              className="pointer-events-none absolute -top-9 left-1/2 z-20 -translate-x-1/2 whitespace-nowrap rounded-full border border-primary/20 bg-white px-3 py-1 text-xs font-semibold text-primary shadow-sm"
              aria-hidden
            >
              Integração
            </span>
          </>
        ) : null}

        <div className="mb-5 flex h-12 items-center justify-center">{item.icon}</div>

        <h3 className="mb-5 text-center text-sm font-semibold text-slate-900">{item.name}</h3>

        {item.comingSoon ? (
          <span className="w-full rounded-lg border border-slate-200 py-2 text-center text-xs font-medium text-slate-500">
            Em breve
          </span>
        ) : (
          <Button
            variant="outline"
            className="h-9 w-full rounded-full border-primary/40 bg-white text-xs font-semibold text-primary hover:bg-primary/5 hover:text-primary"
            asChild
          >
            <Link to={item.isCustom ? "/contato" : "/auth?tab=signup"}>
              {item.isCustom ? "Solicitar" : "Conectar"}
            </Link>
          </Button>
        )}
      </article>
    </div>
  );
}

export const Integrations = () => {
  const { ref, isInView } = useInView({ threshold: 0.08 });
  const [api, setApi] = useState<CarouselApi>();
  const [centerIndex, setCenterIndex] = useState(DEFAULT_CENTER_INDEX);
  const [paused, setPaused] = useState(false);

  const onSelect = useCallback((carouselApi: CarouselApi) => {
    setCenterIndex(carouselApi.selectedScrollSnap());
  }, []);

  useEffect(() => {
    if (!api) return;
    onSelect(api);
    api.on("reInit", onSelect);
    api.on("select", onSelect);
    return () => {
      api.off("reInit", onSelect);
      api.off("select", onSelect);
    };
  }, [api, onSelect]);

  useEffect(() => {
    if (!api || !isInView || paused) return;
    const id = window.setInterval(() => api.scrollNext(), AUTOPLAY_MS);
    return () => window.clearInterval(id);
  }, [api, isInView, paused]);

  useEffect(() => {
    if (!api) return;
    api.scrollTo(DEFAULT_CENTER_INDEX, true);
  }, [api]);

  return (
    <section
      className="relative overflow-hidden border-t border-slate-200/80 bg-white py-20 md:py-28"
      aria-labelledby="integrations-heading"
    >
      <div className="container relative mx-auto max-w-6xl px-4">
        <div
          ref={ref}
          className={cn(
            "mx-auto mb-12 max-w-3xl text-center transition-all duration-700 md:mb-16",
            isInView ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0",
          )}
        >
          <h2
            id="integrations-heading"
            className="text-3xl font-bold tracking-tight text-slate-900 md:text-4xl lg:text-5xl"
          >
            Integrações <span className="text-primary">sem atrito</span>
          </h2>
          <p className="mt-4 text-base leading-relaxed text-slate-600 md:text-lg">
            Conecte canais, modelos de IA e o ERP que sua empresa já usa. Tudo no mesmo painel, com
            configuração visual e suporte da equipe FabChat quando precisar de algo sob medida.
          </p>
          <p className="mt-3 text-sm text-slate-500">
            <Link to="/whatsapp/agente-ia" className="font-medium text-sky-700 hover:underline">
              WhatsApp
            </Link>
            {" · "}
            <Link to="/instagram/agente-ia" className="font-medium text-sky-700 hover:underline">
              Instagram
            </Link>
            {" · "}
            <Link to="/integracoes/crm" className="font-medium text-sky-700 hover:underline">
              CRM
            </Link>
            {" · "}
            <Link
              to="/qualificacao-de-leads-no-whatsapp"
              className="font-medium text-sky-700 hover:underline"
            >
              Qualificação de leads
            </Link>
          </p>
        </div>

        <div
          className={cn(
            "relative border-b border-slate-200/90 py-10 transition-all duration-700 md:py-12",
            isInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0",
          )}
          style={{ transitionDelay: "100ms" }}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <Carousel
            opts={{
              loop: true,
              align: "center",
              duration: 32,
              skipSnaps: false,
            }}
            setApi={setApi}
            className="w-full"
          >
            <CarouselContent className="-ml-3 flex items-end pb-10 pt-12 md:-ml-4 md:pb-12 md:pt-14">
              {integrations.map((item, index) => (
                <CarouselItem
                  key={item.id}
                  className="flex basis-auto justify-center pl-3 md:pl-4"
                >
                  <DogEarCard
                    item={item}
                    isCenter={index === centerIndex}
                    isInView={isInView}
                  />
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        </div>

        <p
          className={cn(
            "mx-auto mt-6 max-w-2xl text-center text-sm text-slate-600 transition-all duration-700",
            isInView ? "opacity-100" : "opacity-0",
          )}
        >
          Precisa integrar com TOTVS, Sankhya, Omie ou outro ERP?{" "}
          <Link to="/contato" className="font-semibold text-primary hover:underline">
            Fale com a gente
          </Link>{" "}
          para um conector personalizado com sua operação.
        </p>
      </div>
    </section>
  );
};
